/**
 * Who is a super admin of the analytics view (/admin).
 *
 * Source of truth for the admin identifiers used by the sign-in screen
 * (detectAdminByEmail) and by useIsSuperAdmin. Both import from here, never
 * keep a copy.
 *
 * Checked on Directus 11.17.4 (agent `dane`, DIRECTUS_ANALYTICS_SCHEMA.md
 * section 1 and risk 5):
 * - 86a762b0-... is the role "Administrator" (its policy has admin_access).
 * - cb6544de-... is NOT a role: it is a user id with the Administrator policy
 *   attached directly (directus_access.user). A `user.role` check never
 *   matches it, so it lives in its own list.
 *
 * Directus 11 grants rights through policies, so the primary check in
 * useIsSuperAdmin is `admin_access` from /policies/me/globals. These lists
 * are the fallback when that endpoint is unavailable.
 */
export const ADMIN_ROLE_IDS = Object.freeze([
  '86a762b0-b895-4690-a021-5bf77dc223d1' // role "Administrator" (legacy admin)
]);

export const ADMIN_USER_IDS = Object.freeze([
  'cb6544de-83d9-441d-b894-7dc51116db88' // user with the Administrator policy attached directly
]);

const ADMIN_ROLE_ID_SET = new Set(ADMIN_ROLE_IDS);
const ADMIN_USER_ID_SET = new Set(ADMIN_USER_IDS);

/** Directus returns `role` as a plain id or as an expanded object. */
export const extractRoleId = (role) => {
  if (!role) return null;
  if (typeof role === 'string') return role;
  if (typeof role === 'object' && role.id) return String(role.id);
  return null;
};

export const isAdminRoleId = (roleId) => Boolean(roleId) && ADMIN_ROLE_ID_SET.has(String(roleId));

export const isAdminUserId = (userId) => Boolean(userId) && ADMIN_USER_ID_SET.has(String(userId));

/**
 * Legacy sign-in check: before the split both identifiers lived in one set
 * and the sign-in screen compared the user's role against it. Kept as is so
 * detectAdminByEmail behaves exactly like before.
 */
export const isKnownAdminIdentifier = (value) => isAdminRoleId(value) || isAdminUserId(value);

/** Fallback check on a `/users/me?fields=id,role` payload. */
export const isSuperAdminUser = (user) => {
  if (!user) return false;
  return isAdminRoleId(extractRoleId(user.role)) || isAdminUserId(user.id);
};
